import { useEffect, useState } from "react";
import cx from "classnames";
import { faFilter } from "@fortawesome/pro-regular-svg-icons";

import { parseNumber } from "common/utils/numbers";
import { useMatch } from "common/router/hooks";
import Spinner from "common/components/Spinner";
import Icon from "common/components/Icon";
import { iconForCategorySlug } from "common/constants/categoryIcons";
import { FetchError } from "common/fetch/types";
import { Optional, WithStringValues } from "common/utils/types";
import { useListSearchState } from "common/hooks";
import { Feature } from "common/types";

import Page from "client/common/components/layouts/Page";
import Container from "client/common/components/layouts/Container";
import Pagination from "client/common/components/layouts/Pagination";
import Results from "client/common/components/results/Results";
import LoadingPage from "client/common/components/loading/LoadingPage";
import Error404Page from "client/common/components/errors/Error404Page";
import { useClinic, useFeatures } from "client/clinics/hooks";
import Body from "client/common/components/layouts/Body";
import { Paths } from "client/routes";
import PillButton from "client/common/components/buttons/PillButton";
import { useBreakpoints } from "client/common/hooks";
import { useProductLinesViewOnVisible, useViewItemListOnVisible } from "client/analytics/utils";
import {
  ProductLineListSource,
  ProductListFilterSurface,
  ProductListSource,
} from "client/analytics/types";

import ProductLineCard from "./ProductLineCard";
import CategoryContentLookup from "./CategoryContent";
import { useFilterPanel, useProductLineSearchQuery, useCategoryQuery } from "../hooks";
import { useCategoryProductLineFilterDefinitions } from "../hooks/filterDefinitions";
import { useFilterChangeFrom } from "../hooks/utils";
import { DEFAULT_PAGE_SIZE } from "../constants/filterDefinitions";
import {
  EMPTY_CATEGORY_PRODUCT_LINE_SELECTION,
  getCategoryProductLineFiltersFromSearch,
} from "../utils";
import BrowseSupport from "./BrowseSupport";
import PreFooter from "./PreFooter";
import AppliedFilterTags from "./AppliedFilterTags";
import FilterPanelModal from "./FilterPanelModal";
import FilterLayout from "./FilterLayout";
import FilterPanel from "./FilterPanel";
import SearchCount from "./SearchCount";
import {
  ProductLineSearch,
  ProductLineSearchFilters,
  SelectedCategoryProductLineFilters,
} from "../types";

import styles from "../styles/CategoryPage.module.less";

type CategorySearchUpdate = Partial<WithStringValues<Paths["products"]["category"]["Search"]>>;

export const initialSearch = {
  ...EMPTY_CATEGORY_PRODUCT_LINE_SELECTION,
  page: 1,
};

type CategoryProductLinesProps = {
  id: number;
  productLines: Optional<ProductLineSearch>;
  isLoading: boolean;
  isRefetching: boolean;
  error: Optional<FetchError>;
  filters: SelectedCategoryProductLineFilters;
  onSearchChange: (search: CategorySearchUpdate) => void;
  onToggleFilterModal: () => void;
};

const CategoryProductLines = ({
  id,
  productLines,
  isLoading,
  isRefetching,
  error,
  filters,
  onSearchChange,
  onToggleFilterModal,
}: CategoryProductLinesProps) => {
  const { isPhoneAndSmaller } = useBreakpoints();
  const itemListRef = useViewItemListOnVisible(
    productLines?.results,
    ProductLineListSource.CategoryPage,
  );
  const productLinesViewRef = useProductLinesViewOnVisible(
    productLines?.results,
    ProductLineListSource.CategoryPage,
    id,
  );

  const definitions = useCategoryProductLineFilterDefinitions();
  const panel = useFilterPanel<SelectedCategoryProductLineFilters, ProductLineSearchFilters>({
    definitions,
    response: productLines,
    filters,
    onChange: onSearchChange,
    listSource: ProductListSource.CategoryPage,
    listContext: { categoryId: id },
  });
  const { filterOptions, showFilterPanel, hasSelectedFilters } = panel;
  const hasAnyFilterOptions = Object.values(filterOptions ?? {}).some((options) => options.length);

  const renderContent = () => {
    if (isLoading) {
      return <Results.Loading description="Loading results" />;
    }

    if (!productLines || error) {
      return <Results.FetchError error={error} entityName="results" />;
    }

    if (!productLines.results.length) {
      return (
        <Results.None
          header="No products found"
          description={
            hasSelectedFilters ? "No products match the filters you have applied." : undefined
          }
        />
      );
    }

    return (
      <div ref={itemListRef} className={styles.search}>
        <div className={cx(styles.products, showFilterPanel && styles.productsNarrow)}>
          {productLines.results.map((productLine, index) => (
            <div
              key={productLine.id}
              ref={index === 0 ? productLinesViewRef : undefined}
              className={styles.product}
            >
              {isPhoneAndSmaller ? (
                <ProductLineCard.Compact
                  productLine={productLine}
                  eventViewName={ProductLineListSource.CategoryPage}
                  eventCategoryId={id}
                />
              ) : (
                <ProductLineCard
                  productLine={productLine}
                  eventViewName={ProductLineListSource.CategoryPage}
                  eventCategoryId={id}
                />
              )}
            </div>
          ))}
        </div>
        <Pagination
          paginator={productLines.paginator}
          onPageChange={(page) => onSearchChange({ page })}
          entityName="result"
        />
      </div>
    );
  };

  return (
    <FilterLayout panel={showFilterPanel && <FilterPanel.Rail {...panel} />}>
      <div className={styles.content}>
        <div className={styles.info}>
          <div className={styles.label}>
            {isRefetching ? <Spinner /> : <SearchCount paginator={productLines?.paginator} />}
          </div>
          {filterOptions && hasAnyFilterOptions && !showFilterPanel && (
            <div className={styles.actions}>
              <AppliedFilterTags<SelectedCategoryProductLineFilters, ProductLineSearchFilters>
                definitions={definitions}
                options={filterOptions}
                value={filters}
                onChange={panel.changeFrom(ProductListFilterSurface.AppliedFilters)}
              />
              <PillButton variant="secondary" size="s" type="button" onClick={onToggleFilterModal}>
                <span className={styles.filter}>
                  <Icon icon={faFilter} /> Filter
                </span>
              </PillButton>
            </div>
          )}
        </div>
        <FilterPanel.AppliedRow {...panel} />
        {renderContent()}
      </div>
    </FilterLayout>
  );
};

const CategoryPage = () => {
  const match = useMatch<Paths["products"]["category"]>();
  const clinic = useClinic();
  const features = useFeatures();
  const isCarouselV2 = features[Feature.CarouselV2];
  const parsedId = parseNumber(match.params.id, 0);

  const [search, setSearch] = useListSearchState<Paths["products"]["category"]>(initialSearch);

  const [isFiltering, setIsFiltering] = useState(false);
  const definitions = useCategoryProductLineFilterDefinitions();

  const { data: category, isLoading: isCategoryLoading } = useCategoryQuery(parsedId);

  const filters = getCategoryProductLineFiltersFromSearch(search);
  const changeFrom = useFilterChangeFrom<
    SelectedCategoryProductLineFilters,
    ProductLineSearchFilters
  >({
    definitions,
    filters,
    onChange: setSearch,
    listSource: ProductListSource.CategoryPage,
    listContext: { categoryId: parsedId },
  });

  const {
    data: productLines,
    error: productLineError,
    isRefetching: isRefetchingProductLines,
    isLoading: isLoadingProductLines,
  } = useProductLineSearchQuery({
    ...filters,
    category_ids: [parsedId],
    important_first: true,
    page_size: DEFAULT_PAGE_SIZE,
    q: "",
    sort: "",
    page: parseNumber(search.page, 1),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [search.page]);

  if (isCategoryLoading) {
    return <LoadingPage />;
  }

  if (!category) {
    return <Error404Page />;
  }

  const CategoryContent = CategoryContentLookup[category.slug] ?? CategoryContentLookup.default;

  return (
    <Page
      style={{
        background: `radial-gradient(farthest-corner at 30% 20%, ${CategoryContent.gradient.start} 0%, ${CategoryContent.gradient.stop} 50%)`,
      }}
      className={styles.page}
    >
      <div>
        <Container>
          <div className={styles.top}>
            <div className={styles.heading}>
              <h1 className={styles.title}>{category.extended_name}</h1>
              <div className={styles.description}>{category.description}</div>
            </div>
            {isCarouselV2 ? (
              <Icon className={styles.icon} icon={iconForCategorySlug(category.slug)} />
            ) : (
              <CategoryContent.Icon className={styles.icon} />
            )}
          </div>
        </Container>
        <div className={styles.main}>
          <Body>
            <CategoryProductLines
              id={parsedId}
              productLines={productLines}
              filters={filters}
              isLoading={isLoadingProductLines}
              isRefetching={isRefetchingProductLines}
              error={productLineError}
              onSearchChange={setSearch}
              onToggleFilterModal={() => setIsFiltering(!isFiltering)}
            />
            {productLines && (
              <FilterPanelModal
                definitions={definitions}
                options={productLines.filters}
                isVisible={isFiltering}
                onClose={() => setIsFiltering(false)}
                value={filters}
                onChange={changeFrom(ProductListFilterSurface.Drawer)}
              />
            )}
          </Body>
        </div>
        <div className={styles.support}>
          <Container>
            <BrowseSupport />
          </Container>
        </div>
      </div>
      <PreFooter className={styles.preFooter} image={clinic.storefront_prefooter_image} />
    </Page>
  );
};

export default CategoryPage;
